// static/js/components/ui/AttachmentIndicator.jsx

/**
 * Displays an indicator for each type of data attached to a message, providing visual labels and removal options.
 *
 * This component is designed to enhance user interaction within applications that handle various data attachments
 * by visually representing each attachment type (e.g., CSV, Image, JSON, Document) alongside the message. 
 * Each attachment is represented by an icon and label, and includes a remove button which, when clicked, triggers 
 * a callback function to handle the removal of the attachment.
 *
 * Usage:
 * - The component accepts an object detailing the attachment types and their presence, and a function to handle 
 *   the removal of an attachment.
 * - It dynamically generates visual elements for each attached data type that is marked as present.
 * - If no attachments are present, the component renders nothing, thereby not affecting the layout.
 *
 * Props:
 * - `attachedTypes`: An object with keys as attachment types and boolean values indicating presence.
 * - `onRemove`: A function that is called with the attachment type as an argument when the remove button is clicked.
 *
 * Styling:
 * - The component styles include a flexible wrapping container for attachment indicators,
 *   which are styled as rounded pills with icons, labels, and a hoverable remove button.
 *
 * Example:
 * <AttachmentIndicator
 *   attachedTypes={{ csv: true, image: true, json: false, markdown: true }}
 *   onRemove={handleRemoveAttachment}
 * />
 */

import React from 'react';
import { Paperclip, X } from 'lucide-react';

/**
 * AttachmentIndicator Component
 * 
 * Displays an indicator showing what types of data are attached to a message,
 * with the ability to remove attachments.
 * 
 * Props:
 * @param {Object} attachedTypes - Object indicating which types of data are attached
 * @param {Function} onRemove - Callback when an attachment is removed
 */
const AttachmentIndicator = ({ attachedTypes = {}, onRemove }) => {
  if (!attachedTypes || Object.keys(attachedTypes).length === 0) {
    return null;
  }

  const getAttachmentLabel = (type) => {
    switch (type) {
      case 'csv':
        return 'CSV Data';
      case 'image':
        return 'Image';
      case 'json':
        return 'JSON Data';
      case 'markdown':
        return 'Document';
      default:
        return type;
    }
  };

  return (
    <div className="flex flex-wrap gap-2 mt-2">
      {Object.entries(attachedTypes).map(([type, isAttached]) => {
        if (!isAttached) return null;
        return (
          <div
            key={type}
            className="flex items-center gap-1 px-2 py-1 rounded-full bg-primary-700/20 text-xs text-primary-text"
          >
            <Paperclip size={12} />
            <span>{getAttachmentLabel(type)}</span>
            <button
              onClick={() => onRemove(type)}
              className="ml-1 p-1 rounded-full hover:bg-primary-700/40 transition-colors"
              title="Remove attachment"
            >
              <X size={12} />
            </button>
          </div>
        );
      })}
    </div>
  );
};

export default AttachmentIndicator;
